import { type HttpClient, HttpErrorResponse, type HttpResponse } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from '@environment';
import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { type PutOutcome } from './save-machine';
import { SHEETS_STRINGS, fill } from '@sharedlib/sheets-editor/sheets.strings';

/** §5.4: "timeout of 20,000 ms" counts as SAVE_RETRYABLE. */
export const SAVE_TIMEOUT_MS = 20_000;

interface ErrorBody {
  readonly error?: { readonly code?: string; readonly message?: string; readonly details?: Record<string, unknown> };
}

/**
 * The response of `PUT …/workbooks/{id}/snapshot` as SM-SAVE's event (§5.4): 200 SAVE_OK, 409
 * SAVE_CONFLICT with who wrote the revision it lost to, 423 SAVE_TRASHED, a network failure or
 * 5xx SAVE_RETRYABLE, and everything else SAVE_FATAL with the server's own message as the reason
 * (413 and 422 are the ones §5.4 names; a 400 or 404 cannot be fixed by retrying either).
 */
/** Appendix E's message for UNAUTHENTICATED (401). */
export const SIGN_IN_REASON = SHEETS_STRINGS['Sign in to continue.'];

export function outcomeOf(status: number, body: unknown): PutOutcome {
  if (status === 200) return { kind: 'ok', revision: Number((body as { revision?: unknown })?.revision) };
  const error = (body as ErrorBody | null)?.error;
  if (status === 409) {
    return {
      kind: 'conflict',
      actor: error?.details?.['lastActor'] === 'stitchy' ? 'stitchy' : 'user',
      revision: Number(error?.details?.['currentRevision']),
    };
  }
  if (status === 423) return { kind: 'trashed' };
  // The gateway's auth guard answers 401 with its own body, so the reason is Appendix E's for UNAUTHENTICATED
  // rather than "The server refused the save (401)."
  if (status === 401) return { kind: 'fatal', reason: error?.message ?? SIGN_IN_REASON };
  if (status === 0 || status >= 500) {
    const afterMs = Number(error?.details?.['retryAfterMs']);
    return afterMs > 0 ? { kind: 'retryable', afterMs } : { kind: 'retryable' };
  }
  return { kind: 'fatal', reason: error?.message ?? fill('The server refused the save ({status}).', { status: String(status) }) };
}

/** Sends one save (§5.2) and classifies the result. Never rejects. */
export interface SnapshotSave {
  readonly workbookId: string;
  readonly snapshot: unknown;
  readonly base: number;
  readonly saveId: string;
  readonly editorSessionId: string;
  /**
   * The workbook's own tenant, named on the request (`OWNING_SPACE_ID`). Without it the spaces
   * interceptor stamps the space current when the request goes, and SM-SAVE's retries and the save
   * after a close outlive the editor: after a switch to another space, the retry went there, 404,
   * fatal, and the edits were lost.
   */
  readonly tenantId?: string | null;
}

export async function putSnapshot(
  http: HttpClient,
  { workbookId, snapshot, base, saveId, editorSessionId, tenantId }: SnapshotSave,
): Promise<PutOutcome> {
  try {
    const response: HttpResponse<unknown> = await firstValueFrom(
      http
        .put(`${environment.baseUrl}/api/sheets/workbooks/${workbookId}/snapshot`, { snapshot }, {
          headers: { 'If-Match': `"${base}"`, 'X-Save-Id': saveId, 'X-Editor-Session-Id': editorSessionId },
          observe: 'response',
          context: owningTenantContext(tenantId),
        })
        .pipe(timeout(SAVE_TIMEOUT_MS)),
    );
    return outcomeOf(response.status, response.body);
  } catch (error) {
    if (error instanceof HttpErrorResponse) return outcomeOf(error.status, error.error);
    // The timeout, or a failure below HTTP.
    return { kind: 'retryable' };
  }
}

/**
 * §8.4's upload, `POST …/workbooks/{id}/assets` with the multipart part `file`, as the uploader
 * the drawing plugins' image I/O calls (`StitchImageIoService`). Rejects on any failure, so the
 * editor shows the engine's own "could not insert" state rather than an image that was never stored.
 */
export function uploadImageTo(http: HttpClient, workbookId: string, tenantId?: string | null): (file: File) => Promise<{ url: string }> {
  return async (file) => {
    const form = new FormData();
    form.append('file', file, file.name);
    const body = await firstValueFrom(
      http
        .post<{ url: string }>(`${environment.baseUrl}/api/sheets/workbooks/${workbookId}/assets`, form, { context: owningTenantContext(tenantId) })
        .pipe(timeout(SAVE_TIMEOUT_MS)),
    );
    return { url: body.url };
  };
}

/**
 * §8.4: a saved image by its wire URL (`/api/sheets/assets/<id>`), which is relative to the API.
 * Through the app's HttpClient, so it carries the session and the CSRF header like any other call.
 */
export function loadAssetFrom(http: HttpClient, tenantId?: string | null): (wireUrl: string) => Promise<Blob> {
  return (wireUrl) =>
    firstValueFrom(http.get(`${environment.baseUrl}${wireUrl}`, { responseType: 'blob', context: owningTenantContext(tenantId) }).pipe(timeout(SAVE_TIMEOUT_MS)));
}
